<template lang="pug">
    span(
        role="combobox",
        class="_field _fldd",
        :class="_class",
        :style="{ width, height }",
        :aria-expanded="expanded",
        :aria-owns="_id",
        v-on="__fwd")
        template(v-if="multiple", v-for="(o,i) in selectedOptions")
            ib-tag(deletable, :disabled="disabled", @delete="_rmtag(o)") {{o.textContent}}
            | {{ filtered || i < selectedOptions.length-1 ? '&thinsp;' : null }}
        span(class="_fldi") &zwnj;
            label(
                :for="'_ddi' + _uid",
                class="_fldl",
                :class="{ _dock: dock }") {{ placeholder }}
            input(
                v-if="filtered",
                v-model="filter",
                ref="_in",
                class="_fldin",
                :id="'_ddi' + _uid",
                :name="name",
                :required="required",
                :readonly="readonly",
                :disabled="disabled",
                :inert="inert",
                :tabindex="inert ? -1 : tabindex",
                :aria-controls="_id",
                autocomplete="new-password",
                autocorrect="off",
                autocapitalize="off",
                v-on="__on",
                @keydown="_keydown",
                @input="input")
            span(
                v-else-if="!native",
                v-html="!multiple && selectedOptions.length ? selectedOptions[0].innerHTML : placeholder ? $string.escapeHTML(placeholder) : '&zwnj;'",
                ref="_in",
                class="_fldis",
                :class="{ _dock: !dock }",
                :tabindex="inert || disabled ? null : (tabindex || 0)",
                v-on="__on",
                @keydown="_keydown")

            // Native picker - TODO:REMOVE
            select(v-else,
                ref="_in",
                class="_fldin",
                size="1",
                :id="'_ddi' + _uid",
                :name="name",
                :multiple="multiple",
                :required="required",
                :disabled="disabled",
                :readonly="readonly",
                :tabindex="inert ? -1 : tabindex",
                v-on="__on",
                @change="_change")
                template(v-for="opt in data")
                    optgroup(v-if="opt.child", :label="opt.label")
                        option(v-for="sopt in opt.child", :value="getval(sopt)")
                            slot(v-bind="sopt") {{typeof sopt === 'object' ? sopt.label : sopt}}
                    option(v-else, :value="getval(opt)")
                        slot(v-bind="opt") {{typeof opt === 'object' ? opt.label : opt}}

        // Emulated picker
        ul(v-if="!native", ref="ul", role="listbox", :id="_id", class="_ddl")
            template(v-for="opt in data")
                template(v-if="opt.child")
                    li
                        slot(v-bind="opt") {{opt.label}}
                    li(v-for="sopt in opt.child",
                        role="option",
                        class="_ind",
                        :data-value="getval(sopt)")
                        slot(v-bind="sopt") {{typeof sopt === 'object' ? sopt.label : sopt}}
                template(v-else)
                    li(
                        role="option",
                        :data-value="getval(opt)")
                        slot(v-bind="opt") {{typeof opt === 'object' ? opt.label : opt}}
</template>

<script>
    import $device from '../../prototype/device';
    import entry from './ib-form-entry.vue';
    import telemetry from '../private/telemetry';
    import picker from './ib-dropdown-pick';

    // older browsers have buggy or missing implementation of selectedOptions
    const haveSelectedOptions = platform =>
          platform == 'android' ? true
        : platform == 'ios' ? true
        : platform == 'desktop' ? true
        : /(Chrome|Firefox)\/([3-9]\d\.|\d{3})|Safari\/([6-9]\d\d\.|\d{4})/.test(navigator.userAgent);

    const stringify = value => value instanceof Object ? JSON.stringify(value, Object.keys(value).sort()) : '' + value;
    const escape = json => json
        .replace(/\\/g, '\\\\')
        .replace(/"/g, '\\"');

    const ie = /Trident\//.test(navigator.userAgent);


    /**
     * A dropdown control that uses either a native `select` element or emulation depending on device.
     */
    export default {
        name: 'ib-dropdown',

        mixins: [
            entry,
            telemetry
        ],

        props: {
            /**
             * An array of objects, each of which is a list entry with a label and a value.
             * It is possible to nest subentries below an entry by using the `child` property.
             */
            data: {
                type: Array,
                default: () => []
            },
            /**
             * Value of currently selected entry, typically applied with `v-model`.
             */
            value: {
                type: [String, Number, Boolean, Array],
                default: undefined
            },
            /**
             * Member of data entry to emit as value. May be specified as a function.
             * By default, for object entries we emit the `value` member and for non-object entries we emit the entry.
             */
            emit: {
                type: [String, Number, Function],
                default: 'value'
            },
            /**
             * Appearance modifier: `frameless` or `underline`. On Android, `underline` is the default.
             */
            appearance: String,
            /**
             * Allow selection of multiple entries; if true, emits an array.
             */
            multiple: Boolean,
            /**
             * Allow filtering of displayed entries via keyboard input.
             */
            filtered: Boolean,
            /**
             * Use a placeholder in the dropdown box when the value is empty. Useful for initial state prompts.
             */
            placeholder: String
        },

        data() {
            return {
                selectedOptions: [],
                filter: null,
                picker: null,
                validity: null,
                expanded: false,
                tagsWidth: 0,
                ie
            };
        },

        computed: {
            native() {
                return !$device.hasKeyboard && !this.filtered;
            },

            _id() {
                return this.id || '_dd' + this._uid;
            },

            _class() {
                return {
                    _fo: this.focused,
                    _mu: this.multiple,
                    _fi: this.filtered,
                    _da: this.disabled,
                    _inv: this.showvalid && !this.valid,
                    ['_a'+this.align]: this.align,
                    ['_e'+this.appearance]: this.appearance,
                    _ie: ie
                };
            },

            getval() {
                const emit = this.emit;
                return typeof emit === 'function'
                    ? emit
                    : o => typeof o === 'object' ? o[emit] : o;
            },

            attval() {
                return this.native
                    ? o => o.value
                    : o => o.getAttribute('data-value');
            },

            dock() {
                if (this.filtered && this.filter) {
                    return true;
                } else {
                    return this.selectedOptions.length;
                }
            }
        },

        watch: {
            data() {
                this.dirty = true;
                this.$nextTick(this._sync);
            }
        },

        methods: {
            // Filter box input event
            input() {
                if (!this.picker || !this.picker.visible) {
                    this._popen();
                } else {
                    this.picker.setFilter(this.filter);
                }
            },

            // Emulated picker uses filter to display current value
            syncFilter() {
                this.filter = !this.multiple && this.selectedOptions.length ? this.selectedOptions[0].textContent : null;
            },

            // On data or value update and emulated picker, recalculate `this.selectedOptions`
            syncEmu() {
                const value = this.ivalue;
                const sel = value => '[data-value="' + escape(stringify(value)) + '"]';
                let selectedOptions;
                if (this.multiple && value && value.map && value.length) {
                    const node = this.$refs.ul.querySelectorAll(value.map(sel));
                    selectedOptions = [].slice.call(node);
                } else {
                    const node = value == null ? value : this.$refs.ul.querySelector(sel(value));
                    selectedOptions = node ? [node] : [];
                }
                this.selectedOptions = selectedOptions;
                this.syncFilter();
            },

            // On data or value update or user input and native picker, recalculate `this.selectedOptions`
            syncNative() {
                this.selectedOptions = haveSelectedOptions($build.platform)
                    ? [].slice.call(this.$refs._in.selectedOptions)
                    : [].filter.call(this.$refs._in.options, option => option.selected);
            },

            // Picker (native or emulated) change event
            _change() {
                if (this.native) {
                    this.syncNative();
                }
                this._set(this.multiple
                    ? [].map.call(this.selectedOptions, this.attval)
                    : this.attval(this.selectedOptions[0]));
                this.validate();
                this.$emit('change', this.ivalue);
            },

            /**
             * Forward Enter/KeyDown/KeyUp/Backspace to emulated picker
             * @private
             */
            _keydown(e) {
                if (!this.native) {
                    switch (e.which) {
                    case 13:
                        if (this.picker && this.picker.visible && this.multiple) {
                            this.filter = null;
                        }
                        // fall through...

                    case 40:
                        if (!this.picker || !this.picker.visible || this.picker.owner !== this) {
                            this._popen();
                        }
                        // fall through...

                    case 27:
                    case 33:
                    case 34:
                    case 38:
                        if (this.picker && this.picker.visible) {
                            this.picker.key(e.keyCode);
                            e.preventDefault();
                            e.stopPropagation();
                        }
                        break;

                    case 8:
                        if (this.multiple) {
                            const input = this.$refs._in;
                            if (!input.selectionStart && !input.selectionEnd) {
                                const selectedOptions = this.selectedOptions;
                                if (selectedOptions.length) {
                                    this._rmtag(selectedOptions[selectedOptions.length - 1]);
                                    e.preventDefault();
                                }
                            }
                        }
                        break;
                    }
                }
                e.stopPropagation();
            },

            /** @private */
            _mousedown(e) {
                if (!e.button && !this.native && !this.readonly) {
                    if (!this.picker || !this.picker.visible) {
                        this._popen();
                    } else {
                        this._pclose();
                    }
                    // stopPropagation without preventDefault causes misbehavior in Safari here
                    e.preventDefault();
                    e.stopPropagation();
                    this.$refs._in.focus();
                }
            },

            /** @private */
            _focus(e) {
                if (this.filtered) {
                    this.$refs._in.select();
                }
                this.focused = true;
            },

            /** @private */
            _blur(e) {
                this._pclose();
                if (!this.multiple && this.filtered && this.ivalue && this.filter != (this.selectedOptions[0] || {}).textContent) {
                    this._set(null);
                }
                this.focused = false;
            },

            /**
             * Sync DOM on data or value update.
             * @private
             */
            _sync() {
                if (this.dirty) {
                    if (this.native) {
                        const input = this.$refs._in;
                        const value = this.ivalue;
                        if (this.multiple && Array.isArray(value)) {
                            const v = value.map(e => stringify(e));
                            const options = input.options;
                            for (let i = 0; i < options.length; i++) {
                                options[i].selected = v.indexOf(options[i].value) >= 0;
                            }
                        } else {
                            input.value = value == null ? value : stringify(value);
                        }
                        this.syncNative();
                    } else {
                        this._pclose();
                        this.syncEmu();
                    }
                    this.validate();
                    this.dirty = false;
                }
            },

            /**
             * Open the picker.
             * @private
             */
            _popen() {
                this.picker = this.$overlay(picker, {
                    owner: this,
                    anchor: this.$el,
                    value: this.ivalue,
                    filter: this.filtered ? this.filter : '',
                    align: 'grow',
                    _style: {
                        fontSize: this.$util.css(this.$el, 'font-size')
                    }
                });
                this.picker.open();
            },

            /**
             * Close the picker.
             * @private
             */
            _pclose() {
                if (this.picker && this.picker.owner === this) {
                    this.picker.close();
                }
            },

            /**
             * Remove a tag from a multiple select dropdown.
             * @private
             */
            _rmtag(option) {
                const value = this.native ? option.value : option.getAttribute('data-value');
                const valueCopy = this.ivalue.slice ? this.ivalue.slice() : [];
                const filtered = valueCopy.filter(v => v != JSON.stringify(value) && v != value);
                this._set(filtered);
            },

            /**
             * Custom validation, due to emulated component.
             * @private
             */
            validate() {
                let validity = null;
                if (this.error) {
                    validity = this.error;
                } else if (this.required && !this.selectedOptions.length) {
                    validity = this.errorMissing ||
                        (this.multiple ? this.$locale.data.selMissingMul : this.$locale.data.selMissing);
                }
                this.validity = validity;
                this.valid = !validity;
                this.__refresherror();
                return this.valid;
            },

            getvalidity() {
                return this.validity;
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    ._fldd {
        flex-direction: column;
        align-items: stretch;
        width: auto;
        cursor: default;

        > ._fldi {
            border-color: inherit;
        }
        &._da > ._fldi {
            opacity: .5;
        }
        input {
            width: 0;
        }
        ._fldis {
            overflow: hidden;
        }
        ._fldl {
            -webkit-padding-end: @dropdown-arrow-width - 4px;
            padding-inline-end: @dropdown-arrow-width - 4px;
        }

        /** Multiple select */
        &._mu {
            flex-direction: row;
            flex-wrap: wrap;
            width: 24ch;
            height: auto;
            .field-padding(2px);
            ._fldi {
                margin-top: 2px;
                margin-bottom: 2px;
            }
            ._fldin {
                min-width: 4ch;
            }
            ._fldis {
                min-width: 12px;
            }
        }

        &               { .dropdown-default(); }
        &._eframeless   { .dropdown-frameless(); }
    }

    /** Picker list (only while docked in control) */
    ._fldd > ._ddl {
        width: 100%;
        padding-right: @dropdown-arrow-width; /* Space for arrow */
        line-height: 0;
        visibility: hidden;
        > li {
            height: 0;
            padding: 0;
        }
    }

    /** Picker list (whether docked in control or overlay) */
    ._ddl {
        list-style: none;
        margin: 0;
        padding: 0;
        white-space: nowrap;
        > li {
            margin: 0;
            padding: 0 8px;
            &._ind {
                padding-left: 16px;
            }
            &:not([role=option]) {
                font-weight: 600;
            }
        }
    }

    /** Picker overlay */
    ._d2 {
        background-color: @bg-modeless;
        z-index: @z-dropdown;

        & {
            transform-origin: top;
        } &._ptop {
            transform-origin: bottom;
        }
        & {
            transform: scaleY(.01);
        } &._v {
            transform: scaleY(1);
        }
    }
    ._d2n {
        z-index: @z-ovl-dropdown;
    }

    ._ddo {
        width: 100%;
        max-height: 280px;
        overflow-x: hidden;
        overflow-y: auto;
        cursor: default;
        -webkit-overflow-scrolling: touch;

        li {
            .line-height(28);
            &._foc {
                background: @bg-select;
                color: @fg-select;
            }
            &[disabled] {
                opacity: .5;
            }
        }
    }

    [dir=rtl] ._fldd {
        &._acenter ._fldl,
        &._aend ._fldl {
            padding: 0 0 0 20px;
        }
    }

    /** Native picker - TODO:REMOVE */
    select._fldin {
        &._dock,
        &[inert] {
            visibility: hidden;
        }
        &[multiple] {
            color: transparent;
        }
        &:-moz-focusring {
            color: transparent !important;
            text-shadow: 0 0 0 @fg;
            option:disabled {
                text-shadow: none;
            }
        }
    }
    ._fldis {
        &._dock {
            /** visibility: hidden would prevent focus */
            opacity: 0;
        }
    }
</style>

<docs>
    ```jsx
        new Vue({
            data() {
                return {
                    words: "Tax year 2017 maximum amount one may contribute their IRA $5,500 $6,500 you age 50 older Note that contributions made multiple accounts established same beneficial owner contribution limit will be reduce all contributions were made contributions IRAs subject income limits Holders should refer IRS Publication 590 most date information contribution limitations".split(' '),
                    value: [null,[],null,[]]
                };
            },
            template: `
                <ib-context>
                    <p class="after-sm">Basic Example: {{value[0]}}</p>
                    <p><ib-dropdown :data="words" v-model="value[0]" placeholder="Basic Example"/></p>
                    <p class="before-lg after-sm">Multiple Select: {{value[1]}}</p>
                    <p><ib-dropdown :data="words" v-model="value[1]" placeholder="Basic Example" width="24ch" multiple/></p>
                    <p class="before-lg after-sm">Filtered Select: {{value[2]}}</p>
                    <p><ib-dropdown :data="words" v-model="value[2]" placeholder="Basic Example" width="24ch" filtered/></p>
                    <p class="before-lg after-sm">Multiple, Filtered Select: {{value[3]}}</p>
                    <p><ib-dropdown :data="words" v-model="value[3]" placeholder="Basic Example" width="24ch" multiple filtered/></p>
                </ib-context>`
        });
    ```
</docs>
